import React, { useState } from 'react';
//@ts-ignore
import * as styles from './product-specifications.module.css';
//@ts-ignore
import {
    CTAConfiguration,
    KontentStringValue,
    CTALocation,
    KontentProductImage,
    KontentRichText,
} from '../../../types/KontentProps';
import { Specifications } from '../ProductSpecifications/ProductSpecsList';
import { Material } from '../ProductSpecifications/Materials';
import parse from 'html-react-parser';
import RichText from '../Global/RichText';
import {
    optimizeKontentImageUrl,
    buildKontentSrcSet,
    getDisplayDimensions,
} from '../Global/DataUtils';
import LazyImage from '../Global/LazyImage';
import { isRichTextFieldBlank } from '../../global/utility';

type GalleryImages = {
    elements: {
        file?: {
            value: {
                url: string;
            };
        };
        untitled_asset?: {
            value: {
                url: string;
            };
        };
    };
    internal?: {
        type?:
            | 'kontent_item_media___product_image'
            | 'kontent_item_media___360_interior_render'
            | 'kontent_item_media___360_render';
    };
};

type WoodType = {
    id: string;
    elements: {
        image: {
            value: {
                elements: {
                    file: {
                        value: {
                            url: string;
                        };
                    };
                };
            };
        };
        material: {
            value: {
                name: string;
            };
        };
        material_feature: {
            value: {
                name: string;
            }[];
        };
        material_feature___german: {
            value: {
                name: string;
            }[];
        };
        name_for_display: {
            value: string;
        };
    };
    internal: {
        type: string;
    };
};

interface epigraphContent {
    element: {
        cta_configuration__cta_label?: KontentStringValue;
        cta_configuration__cta_location?: CTALocation;
        cta_configuration__cta_window_action?: {
            value: {
                codename: 'same_window' | 'new_window';
            }[];
        };
        cta_copy: KontentStringValue;
        cta_icons: KontentProductImage;
        sku: KontentStringValue;
        ua_code: KontentStringValue;
    };
}

export interface epigraphCTA {
    cta_configuration__cta_label?: KontentStringValue;
    cta_configuration__cta_location?: CTALocation;
    cta_configuration__cta_window_action?: {
        value: {
            codename: 'same_window' | 'new_window';
        }[];
    };
    cta_copy: KontentStringValue;
    cta_icons: KontentProductImage;
    sku: KontentStringValue;
    ua_code: KontentStringValue;
}

interface Props extends CTAConfiguration {
    kontentItemId?: string;
    ctas: {
        value: {
            id: string;
            elements: {
                cta_label: { value: string };
                cta_location: { value: CTALocation[] };
                cta_location_anchor_point: { value: string };
                cta_window_action: {
                    value: { codename: 'same_window' | 'new_window' }[];
                };
            };
        }[];
    };
    contact_panel: {
        value: {
            elements: {
                headline: { value: string };
                description: KontentRichText;
                cta_configuration__cta_location_anchor_point: { value: string };
                cta_configuration__cta_window_action: {
                    value: { codename: 'same_window' | 'new_window' }[];
                };
                cta_configuration__cta_location: { value: CTALocation[] };
                cta_configuration__cta_label: { value: string };
                image: {
                    value: {
                        elements: {
                            file: {
                                value: {
                                    url: string;
                                    description: string | null;
                                    name: string;
                                }[];
                            };
                        };
                    }[];
                };
            };
        }[];
    };
    gallery_images: { value: GalleryImages[] };
    materials: { value: Material[] };
    specs: { value: { elements: Specifications }[] };
    component_anchor_name: {
        value: string;
    };
    disclaimer: {
        value: string;
    };
    qr_codes?: { value: epigraphContent[] };
}

const ProductSpecificationsV2: React.FC<Props> = (props) => {
    const disclaimer = props?.disclaimer?.value;

    // Extract specifications data from the specs prop
    const specificationsData = props.specs?.value?.[0]?.elements;
    const cta_description =
        props.contact_panel?.value[0]?.elements?.description;

    const cta_location =
        props.contact_panel?.value[0]?.elements?.cta_configuration__cta_location
            ?.value[0]?.elements?.slug?.url || '';

    const ctaTarget =
        props.contact_panel?.value[0]?.elements
            ?.cta_configuration__cta_window_action?.value[0]?.codename ===
        'new_window'
            ? '_blank'
            : '_self';

    // Create specifications array from the data structure
    const specifications = [
        {
            id: 'category_1',
            title:
                specificationsData?.category_1_headline?.value ||
                'Specifications',
            is_expanded: false,
        },
        {
            id: 'category_2',
            title: specificationsData?.category_2_headline?.value || '',
            is_expanded: false,
        },
        {
            id: 'category_3',
            title: specificationsData?.category_3_headline?.value || '',
            is_expanded: false,
        },
        {
            id: 'category_4',
            title: specificationsData?.category_4_headline?.value || '',
            is_expanded: false,
        },
        {
            id: 'category_5',
            title: specificationsData?.category_5_headline?.value || '',
            is_expanded: false,
        },
        {
            id: 'category_6',
            title: specificationsData?.category_6_headline?.value || '',
            is_expanded: false,
        },
        {
            id: 'category_7',
            title:
                (specificationsData as any)?.category_7_headline?.value || '',
            is_expanded: false,
        },
    ].filter((spec) => spec.title); // Only include categories that have titles

    const [expandedItems, setExpandedItems] = useState<Set<string>>(
        new Set(
            specifications
                .filter((spec) => spec.is_expanded)
                .map((spec) => spec.id)
        )
    );

    const toggleItem = (id: string) => {
        setExpandedItems((prev) => {
            const newSet = new Set(prev);
            if (newSet.has(id)) {
                newSet.delete(id);
            } else {
                newSet.add(id);
            }
            return newSet;
        });
    };

    return (
        <div
            className={styles.wrapper}
            data-kontent-item-id={props.kontentItemId}
        >
            <div className={styles.container}>
                <div className={styles.specsHeader}>
                    {/* TODO: Hiding this for now. Will probable need to add a new field for this. */}
                    {/* {specificationsData?.category_1_headline?.value && (
                    <h2 className={styles.specsSectionTitle}>
                        {specificationsData?.category_1_headline?.value}
                    </h2>
                )} */}
                    <h3 data-kontent-element-codename="headline">
                        {props?.headline?.value}
                    </h3>
                </div>

                {/* Main Content Section */}
                <div className={styles.mainContent}>
                    {/* Left Column - Specifications */}
                    <div className={styles.specsColumn}>
                        <hr />
                        <div className={styles.specificationsList}>
                            {specifications.map((spec) => {
                                const isExpanded = expandedItems.has(spec.id);
                                return (
                                    <div
                                        key={spec.id}
                                        className={styles.specificationItem}
                                    >
                                        <button
                                            className={
                                                styles.specificationHeader
                                            }
                                            onClick={() => toggleItem(spec.id)}
                                            aria-expanded={isExpanded}
                                        >
                                            <div className={styles.specIcon}>
                                                {(() => {
                                                    const categoryNumber =
                                                        spec.id.split('_')[1];
                                                    const iconData =
                                                        categoryNumber === '1'
                                                            ? specificationsData?.category_1_icon
                                                            : categoryNumber ===
                                                              '2'
                                                            ? specificationsData?.category_2_icon
                                                            : categoryNumber ===
                                                              '3'
                                                            ? specificationsData?.category_3_icon
                                                            : categoryNumber ===
                                                              '4'
                                                            ? specificationsData?.category_4_icon
                                                            : categoryNumber ===
                                                              '5'
                                                            ? specificationsData?.category_5_icon
                                                            : categoryNumber ===
                                                              '6'
                                                            ? specificationsData?.category_6_icon
                                                            : (
                                                                  specificationsData as any
                                                              )
                                                                  ?.category_7_icon;

                                                    const iconUrl = (
                                                        iconData as any
                                                    )?.value?.[0]?.elements
                                                        ?.icon_svg_file
                                                        ?.value?.[0]?.url;

                                                    return iconUrl ? (
                                                        <div
                                                            data-kontent-element-codename={`category_${categoryNumber}_icon`}
                                                        >
                                                            <LazyImage
                                                                src={iconUrl}
                                                                alt=""
                                                                className={
                                                                    styles.iconImage
                                                                }
                                                                width={31}
                                                                height={31}
                                                            />
                                                        </div>
                                                    ) : (
                                                        <div
                                                            className={
                                                                styles.iconPlaceholder
                                                            }
                                                        />
                                                    );
                                                })()}
                                            </div>
                                            <span className={styles.specTitle}>
                                                {spec.title}
                                            </span>
                                            <span className={styles.toggleIcon}>
                                                {isExpanded ? '−' : '+'}
                                            </span>
                                        </button>

                                        {isExpanded && (
                                            <div
                                                className={
                                                    styles.specificationContent
                                                }
                                            >
                                                {(() => {
                                                    const categoryNumber =
                                                        spec.id.split('_')[1];
                                                    // Handle wood types for category_7
                                                    const woodTypesData = (
                                                        specificationsData as any
                                                    )?.wood_types?.value;
                                                    if (
                                                        categoryNumber ===
                                                            '7' &&
                                                        woodTypesData
                                                    ) {
                                                        return (
                                                            <div
                                                                className={
                                                                    styles.woodTypesContainer
                                                                }
                                                            >
                                                                {woodTypesData.map(
                                                                    (
                                                                        woodType: WoodType,
                                                                        idx: number
                                                                    ) => {
                                                                        return (
                                                                            <div
                                                                                key={
                                                                                    woodType.id ||
                                                                                    idx
                                                                                }
                                                                                className={
                                                                                    styles.woodTypeItem
                                                                                }
                                                                            >
                                                                                <div
                                                                                    className={
                                                                                        styles.woodTypeImage
                                                                                    }
                                                                                    data-kontent-element-codename="image"
                                                                                >
                                                                                    <LazyImage
                                                                                        src={
                                                                                            (
                                                                                                woodType
                                                                                                    .elements
                                                                                                    .image
                                                                                                    .value as any
                                                                                            )?.[0]
                                                                                                ?.elements
                                                                                                ?.file
                                                                                                ?.value?.[0]
                                                                                                ?.url ||
                                                                                            ''
                                                                                        }
                                                                                        alt={
                                                                                            woodType
                                                                                                .elements
                                                                                                .name_for_display
                                                                                                .value
                                                                                        }
                                                                                        width={
                                                                                            (
                                                                                                woodType
                                                                                                    .elements
                                                                                                    .image
                                                                                                    .value as any
                                                                                            )?.[0]
                                                                                                ?.elements
                                                                                                ?.file
                                                                                                ?.value?.[0]
                                                                                                ?.width ||
                                                                                            300
                                                                                        }
                                                                                        height={
                                                                                            (
                                                                                                woodType
                                                                                                    .elements
                                                                                                    .image
                                                                                                    .value as any
                                                                                            )?.[0]
                                                                                                ?.elements
                                                                                                ?.file
                                                                                                ?.value?.[0]
                                                                                                ?.height ||
                                                                                            200
                                                                                        }
                                                                                    />
                                                                                </div>
                                                                                <div
                                                                                    className={
                                                                                        styles.woodTypeContent
                                                                                    }
                                                                                >
                                                                                    <h4
                                                                                        className={
                                                                                            styles.woodTypeName
                                                                                        }
                                                                                    >
                                                                                        {
                                                                                            woodType
                                                                                                .elements
                                                                                                .name_for_display
                                                                                                .value
                                                                                        }
                                                                                    </h4>
                                                                                    <ul
                                                                                        className={
                                                                                            styles.woodTypeFeatures
                                                                                        }
                                                                                    >
                                                                                        {woodType.elements.material_feature.value.map(
                                                                                            (
                                                                                                feature: any,
                                                                                                featureIdx: number
                                                                                            ) => (
                                                                                                <li
                                                                                                    key={
                                                                                                        featureIdx
                                                                                                    }
                                                                                                >
                                                                                                    {
                                                                                                        feature.name
                                                                                                    }
                                                                                                </li>
                                                                                            )
                                                                                        )}
                                                                                    </ul>
                                                                                </div>
                                                                            </div>
                                                                        );
                                                                    }
                                                                )}
                                                            </div>
                                                        );
                                                    }

                                                    const measurements =
                                                        specificationsData?.[
                                                            `category_${categoryNumber}_measurements` as keyof typeof specificationsData
                                                        ] as any;

                                                    if (
                                                        measurements?.value &&
                                                        measurements.value
                                                            .length > 0
                                                    ) {
                                                        return measurements.value.map(
                                                            (
                                                                measurement: any,
                                                                idx: number
                                                            ) => (
                                                                <div key={idx}>
                                                                    {measurement
                                                                        .elements
                                                                        ?.weight && (
                                                                        <p>
                                                                            {measurement
                                                                                .elements
                                                                                ?.material
                                                                                ?.value?.[0]
                                                                                ?.elements
                                                                                ?.name_for_display
                                                                                ?.value
                                                                                ? measurement
                                                                                      .elements
                                                                                      .material
                                                                                      .value[0]
                                                                                      .elements
                                                                                      .name_for_display
                                                                                      .value
                                                                                : ''}{' '}
                                                                            weight:{' '}
                                                                            {
                                                                                measurement
                                                                                    .elements
                                                                                    .weight
                                                                                    ?.value
                                                                            }
                                                                        </p>
                                                                    )}
                                                                    {measurement
                                                                        .elements
                                                                        ?.measurement_content && (
                                                                        <div
                                                                            className={
                                                                                styles.measurementContent
                                                                            }
                                                                        >
                                                                            {RichText(
                                                                                measurement
                                                                                    .elements
                                                                                    ?.measurement_content,
                                                                                undefined,
                                                                                undefined,
                                                                                undefined,
                                                                                'measurement_content'
                                                                            )}
                                                                        </div>
                                                                    )}
                                                                    {measurement
                                                                        .elements
                                                                        ?.title && (
                                                                        <p>
                                                                            {
                                                                                measurement
                                                                                    .elements
                                                                                    .title
                                                                                    ?.value
                                                                            }
                                                                            :{' '}
                                                                            {measurement
                                                                                .elements
                                                                                ?.value
                                                                                ?.value ||
                                                                                ''}
                                                                        </p>
                                                                    )}
                                                                    {measurement
                                                                        .elements
                                                                        ?.small_sub_copy && (
                                                                        <div
                                                                            className={
                                                                                styles.measurementSubCopy
                                                                            }
                                                                        >
                                                                            <div
                                                                                dangerouslySetInnerHTML={{
                                                                                    __html:
                                                                                        measurement
                                                                                            .elements
                                                                                            .small_sub_copy
                                                                                            ?.value ||
                                                                                        '',
                                                                                }}
                                                                            />
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            )
                                                        );
                                                    } else {
                                                        return (
                                                            <p>
                                                                No
                                                                specifications
                                                                available for
                                                                this category.
                                                            </p>
                                                        );
                                                    }
                                                })()}
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>

                        {disclaimer && (
                            <div className={styles.specsFooter}>
                                <div className={styles.specsFooterText}>
                                    {parse(disclaimer)}
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Right Column - Consultant */}
                    <div className={styles.rightColumn}>
                        <div className={styles.consultantContainer}>
                            <h3 className={styles.consultantTitle}>
                                {
                                    props.contact_panel?.value[0]?.elements
                                        ?.headline?.value
                                }
                            </h3>
                            {cta_description &&
                                !isRichTextFieldBlank(
                                    cta_description.value
                                ) && (
                                    <div
                                        className={styles.consultantDescription}
                                    >
                                        {RichText(
                                            cta_description,
                                            undefined,
                                            undefined,
                                            undefined,
                                            'cta_description'
                                        )}
                                    </div>
                                )}
                            <a
                                href={cta_location}
                                target={ctaTarget}
                                rel={
                                    ctaTarget === '_blank'
                                        ? 'noopener noreferrer'
                                        : undefined
                                }
                                className={styles.consultantLink}
                            >
                                {props.contact_panel?.value[0]?.elements
                                    ?.cta_configuration__cta_label?.value ||
                                    'CONNECT'}{' '}
                                &gt;
                            </a>
                            <div
                                className={styles.consultantImageContainer}
                                data-kontent-element-codename="image"
                            >
                                {props.contact_panel?.value[0]?.elements?.image
                                    ?.value?.[0]?.elements?.file?.value?.[0]
                                    ?.url ? (
                                    <LazyImage
                                        src={optimizeKontentImageUrl(
                                            props.contact_panel.value[0]
                                                .elements.image.value[0]
                                                .elements.file.value[0].url,
                                            {
                                                w: 400,
                                                fm: 'webp' as const,
                                                q: 95,
                                            }
                                        )}
                                        srcSet={buildKontentSrcSet(
                                            props.contact_panel.value[0]
                                                .elements.image.value[0]
                                                .elements.file.value[0].url,
                                            400,
                                            { fm: 'webp' as const, q: 95 }
                                        )}
                                        sizes="200px"
                                        alt={
                                            props.contact_panel.value[0]
                                                .elements.image.value[0]
                                                .elements.file.value[0].name ||
                                            'Consultant image'
                                        }
                                        className={styles.consultantImage}
                                        {...getDisplayDimensions(
                                            (
                                                props.contact_panel.value[0]
                                                    .elements.image.value[0]
                                                    .elements.file
                                                    .value[0] as any
                                            ).width,
                                            (
                                                props.contact_panel.value[0]
                                                    .elements.image.value[0]
                                                    .elements.file
                                                    .value[0] as any
                                            ).height,
                                            400
                                        )}
                                    />
                                ) : (
                                    <div
                                        className={
                                            styles.consultantImagePlaceholder
                                        }
                                    />
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ProductSpecificationsV2;
